import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Play, 
  Pause, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Search,
  FileText,
  Users,
  Tag,
  Brain,
  Database,
  AlertCircle,
  Clock,
  Info
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { API_CONFIG } from '@/config/api';

interface IndexingStatusProps {
  videoId: string;
  token: string;
  onRetry?: () => void;
}

interface IndexingStage {
  stage: string;
  name: string;
  progress: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  weight: number;
}

interface IndexingStatus {
  video_id: string;
  current_stage: string;
  stage_progress: number;
  overall_progress: number;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  frame_count: number;
  segment_count: number;
  stage_details: IndexingStage[];
  stage_data: any;
  search_indexed: string;
  last_updated: string | null;
}

const STAGE_ICONS = {
  'FRAME_EXTRACTION': <FileText className="h-4 w-4" />,
  'OBJECT_DETECTION': <Search className="h-4 w-4" />,
  'TRANSCRIPTION': <FileText className="h-4 w-4" />,
  'DIARIZATION': <Users className="h-4 w-4" />,
  'ENTITY_EXTRACTION': <Tag className="h-4 w-4" />,
  'EMBEDDING_GENERATION': <Brain className="h-4 w-4" />,
  'INDEX_STORAGE': <Database className="h-4 w-4" />,
};

export default function IndexingStatus({ videoId, token, onRetry }: IndexingStatusProps) {
  const [status, setStatus] = useState<IndexingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [shouldStopPolling, setShouldStopPolling] = useState(false);
  const { toast } = useToast();

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${videoId}/indexing-status-detailed`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`Video ${videoId} not found - indexing status unavailable`);
          setStatus(null);
          setErrorCount(0);
          return;
        }
        throw new Error(`Failed to fetch indexing status: ${response.status}`);
      }

      const data = await response.json();
      setStatus(data);
      setErrorCount(0);
      setShouldStopPolling(false);
    } catch (error) {
      console.error('Error fetching indexing status:', error);
      setErrorCount(prev => prev + 1);
      
      if (errorCount >= 2) {
        setShouldStopPolling(true);
        toast({
          title: "Error",
          description: "Stopped polling due to repeated errors. Please refresh manually.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch indexing status",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  }, [videoId, token, errorCount, toast]);

  const handleReindex = async () => {
    try {
      setReindexing(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${videoId}/reindex`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start reindexing');
      }

      const result = await response.json();
      
      toast({
        title: "Success",
        description: "Reindexing started successfully",
      });

      // Reset polling and fetch new status
      setShouldStopPolling(false);
      setErrorCount(0);
      setTimeout(fetchStatus, 2000);
      onRetry?.();
    } catch (error) {
      console.error('Error starting reindexing:', error);
      toast({
        title: "Error",
        description: "Failed to start reindexing",
        variant: "destructive"
      });
    } finally {
      setReindexing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Only poll if indexing is in progress
    const shouldPoll = () => {
      return status && (
        status.current_stage === 'FRAME_EXTRACTION' ||
        status.current_stage === 'OBJECT_DETECTION' ||
        status.current_stage === 'TRANSCRIPTION' ||
        status.current_stage === 'DIARIZATION' ||
        status.current_stage === 'ENTITY_EXTRACTION' ||
        status.current_stage === 'EMBEDDING_GENERATION' ||
        status.current_stage === 'INDEX_STORAGE'
      );
    };

    // Poll for updates every 10 seconds, but only if indexing is in progress and not stopped due to errors
    const interval = setInterval(() => {
      if (shouldPoll() && !shouldStopPolling) {
        fetchStatus();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [videoId, token, status?.current_stage, shouldStopPolling, fetchStatus]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Indexing Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Indexing Status Unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Unable to load indexing status for this video.
          </p>
          <Button
            onClick={() => {
              setShouldStopPolling(false);
              setErrorCount(0);
              fetchStatus();
            }}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isCompleted = status.current_stage === 'COMPLETED';
  const isFailed = status.current_stage === 'FAILED';
  const isInProgress = !isCompleted && !isFailed && status.current_stage !== 'NOT_STARTED';
  const canReindex = isFailed || status.current_stage === 'NOT_STARTED' || status.search_indexed === 'false';

  const getStatusIcon = (stageStatus: string) => {
    switch (stageStatus) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Pause className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Indexing Status
          </div>
          {isInProgress && (
            <Badge variant="secondary" className="animate-pulse">
              Processing
            </Badge>
          )}
          {isCompleted && (
            <Badge variant="default" className="bg-green-500">
              Completed
            </Badge>
          )}
          {isFailed && (
            <Badge variant="destructive">
              Failed
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {isInProgress && `Currently ${status.current_stage.replace('_', ' ').toLowerCase()} (${status.stage_progress}%)`}
          {isCompleted && `Indexing completed successfully`}
          {isFailed && `Indexing failed: ${status.error}`}
          {status.current_stage === 'NOT_STARTED' && 'Indexing not started'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Overall Progress</span>
            <span>{status.overall_progress}%</span>
          </div>
          <Progress value={status.overall_progress} className="h-2" />
        </div>

        {/* Stage Details */}
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Indexing Stages</h4>
          {status.stage_details?.map((stage, index) => {
            const isCurrentStage = stage.stage === status.current_stage && isInProgress;
            
            return (
              <div
                key={stage.stage}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isCurrentStage ? 'bg-blue-50 border-blue-200' : ''
                }`}
              >
                {getStatusIcon(stage.status)}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{stage.name}</span>
                    {isCurrentStage && (
                      <Badge variant="outline" className="text-xs">
                        {status.stage_progress}%
                      </Badge>
                    )}
                    {stage.status === 'completed' && (
                      <Badge variant="outline" className="text-xs text-green-600">
                        100%
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Weight: {stage.weight}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Stats */}
        {(status.frame_count > 0 || status.segment_count > 0) && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="text-2xl font-bold">{status.frame_count}</div>
              <div className="text-xs text-muted-foreground">Frames Extracted</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{status.segment_count}</div>
              <div className="text-xs text-muted-foreground">Segments Indexed</div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {status.error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">Error</span>
            </div>
            <p className="text-sm text-red-600 mt-1">{status.error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4 border-t">
          <Button
            onClick={() => {
              setShouldStopPolling(false);
              setErrorCount(0);
              fetchStatus();
            }}
            variant="outline"
            size="sm"
            className="flex-1"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canReindex && (
            <Button
              onClick={handleReindex}
              disabled={reindexing}
              size="sm"
              className="flex-1"
            >
              {reindexing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {reindexing ? 'Starting...' : 'Reindex'}
            </Button>
          )}
        </div>

        {/* Timing Info */}
        {(status.started_at || status.completed_at || status.last_updated) && (
          <div className="text-xs text-muted-foreground space-y-1">
            {status.started_at && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Started: {new Date(status.started_at).toLocaleString()}
              </div>
            )}
            {status.completed_at && (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Completed: {new Date(status.completed_at).toLocaleString()}
              </div>
            )}
            {status.last_updated && (
              <div className="flex items-center gap-1">
                <Info className="h-3 w-3" />
                Last Updated: {new Date(status.last_updated).toLocaleString()}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
} 